/**
 * The words for a workflow's lock (AMO-305): who locked it and why, with
 * people named rather than shown as addresses where their name is known.
 *
 * @module components/workflows/WorkflowLockDialog.logic
 */
import type { WorkflowLock, WorkflowLockEvent } from "@amoreg/contracts";

const who = (email: string | null, nameOf: (email: string) => string | null) => (email ? (nameOf(email) ?? email) : "Someone");

/** A lock in one sentence: "Locked by Carol: approved for Q4. Unlock it to change it." */
export function lockSummary(lock: WorkflowLock, nameOf: (email: string) => string | null): string {
  return `Locked by ${who(lock.lockedByEmail, nameOf)}${lock.reason ? `: ${lock.reason}` : ""}. Unlock it to change it; it still runs.`;
}

/**
 * Why this editor cannot lock the workflow yet, or null when they can: locking with their own edits unsaved
 * would set those edits aside (a locked workflow takes no save), so they lock once the edits are saved.
 */
export function lockWaitsForSave(saveState: "saved" | "unsaved" | "saving" | "error"): string | null {
  switch (saveState) {
    case "saved":
      return null;
    case "error":
      return "Your latest changes did not save. Save them, or undo them, before locking.";
    default:
      return "Your latest changes are still saving. Lock it once they are saved.";
  }
}

/**
 * Said when a lock arrives while edits here were unsaved: they could no longer be saved, so the chart went back
 * to the locked version. "Carol locked this workflow (approved for Q4), so your unsaved changes were not kept."
 */
export function lockDiscardedEdits(lock: WorkflowLock, nameOf: (email: string) => string | null): string {
  return `${who(lock.lockedByEmail, nameOf)} locked this workflow${lock.reason ? ` (${lock.reason})` : ""}, so your unsaved changes were not kept.`;
}

/** One line of the history: "Carol locked it (approved for Q4)", "Alice unlocked it". */
export function lockEventLabel(event: WorkflowLockEvent, nameOf: (email: string) => string | null): string {
  return `${who(event.byEmail, nameOf)} ${event.action === "lock" ? "locked" : "unlocked"} it${event.reason ? ` (${event.reason})` : ""}`;
}
